import React from "react";
import { View, Pressable, StyleSheet } from "react-native";
import { t } from "@constants/localization";
import { LOCK, STATUS, eddLabel, getCardActions } from "../helpers";
import { C, RText, Tag, ChevronRight, AddressLine, tpName } from "../theme";

const PATH = "DsRouting";

// Status pill on the overview: the same colours as the D0 card.
const STATUS_TAG = {
  [STATUS.NOT_RECEIVED]: {
    key: "notReceived",
    bg: C.disabledBg,
    color: C.muted,
  },
  [STATUS.NOT_RACKED]: { key: "notRacked", bg: C.amberBg, color: C.amber },
  [STATUS.PACKED]: { key: "packed", bg: C.greenBg, color: C.green },
  [STATUS.ROUTED]: { key: "packed", bg: C.greenBg, color: C.green },
  [STATUS.PICKED]: { key: "pickedByDb", bg: C.blueBg, color: C.blue },
};

const eddText = (date, today) => {
  const label = eddLabel(date, today);
  if (!label) return null;
  const value = label.key
    ? t(`${PATH}/edd${label.key === "today" ? "Today" : "Tomorrow"}`)
    : label.text;
  return t(`${PATH}/edd`, { date: value });
};

/**
 * One TP on "All orders", pixel-matched to the mockup (§10 frame 07):
 * customer name · orders; address; TP id · locality · rack; tags; on the
 * right the "DB · name" pill with an outlined Change, or a chevron when no
 * DB is mapped. Only D0 TPs act (Change, or tap to add a DB); NEXT TPs are
 * read-only.
 */
const AllOrdersRow = ({ item, today, onPick }) => {
  const actions = getCardActions(item);
  const id = `${item.cx_id}-${item.date}`;
  const title = tpName(item);
  const isOfd = item.status === STATUS.OFD;
  const statusTag = isOfd
    ? STATUS_TAG[STATUS.PACKED]
    : STATUS_TAG[item.status] || STATUS_TAG[STATUS.PACKED];
  const ofdPending = !isOfd && Boolean(item.db) && item.is_d0;
  const place = [
    item.area,
    item.racks?.length
      ? t(`${PATH}/rack`, { racks: item.racks.join(", ") })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const edd = eddText(item.date, today);

  return (
    <Pressable
      testID={`ds-routing-all-${id}`}
      disabled={!actions.addDb}
      onPress={() => onPick(item)}
      style={styles.card}
    >
      <View style={styles.info}>
        <View style={styles.head}>
          <RText size={17} weight={700} style={styles.tight}>
            {title}
          </RText>
          <RText size={12} weight={600} color={C.muted}>
            {t(`${PATH}/${item.order_count === 1 ? "order" : "orders"}`, {
              count: String(item.order_count ?? 0),
            })}
          </RText>
        </View>
        <AddressLine address={item.address} />
        {place ? (
          <RText size={12} weight={500} color={C.muted}>
            {place}
          </RText>
        ) : null}
        <View style={styles.tags}>
          {edd ? <Tag label={edd} bg={C.blueBg} color={C.blueLine} /> : null}
          <Tag
            label={t(`${PATH}/status/${statusTag.key}`)}
            bg={statusTag.bg}
            color={statusTag.color}
          />
          {isOfd ? (
            <Tag
              label={t(`${PATH}/status/ofd`)}
              bg={C.greenBg}
              color={C.green}
            />
          ) : null}
          {actions.lock === LOCK.DELIVERED_TODAY ? (
            <Tag
              label={t(`${PATH}/status/deliveredToday`)}
              bg={C.disabledBg}
              color={C.muted}
            />
          ) : null}
          {ofdPending ? (
            <Tag
              label={t(`${PATH}/status/ofdPending`)}
              bg={C.pendingBg}
              color={C.pending}
            />
          ) : null}
        </View>
      </View>

      {item.db ? (
        <View style={styles.side}>
          <Tag
            label={t(`${PATH}/dbLine`, { name: item.db.name || "" })}
            bg={C.blueBg}
            color={C.blue}
            size={10}
            style={styles.dbPill}
          />
          {actions.change ? (
            <Pressable
              testID={`ds-routing-all-change-${id}`}
              onPress={() => onPick(item)}
              hitSlop={8}
              style={styles.change}
            >
              <RText size={12} weight={700} color={C.blueLine}>
                {t(`${PATH}/actions/change`)}
              </RText>
            </Pressable>
          ) : null}
          {actions.lock === LOCK.LIVE_OFD ? (
            <Tag
              label={t(`${PATH}/actions/onTrip`)}
              bg={C.blueBg}
              color={C.blue}
              size={10}
              style={styles.dbPill}
            />
          ) : null}
        </View>
      ) : actions.addDb ? (
        <View style={styles.chevron}>
          <ChevronRight size={18} />
        </View>
      ) : null}
    </Pressable>
  );
};

export default AllOrdersRow;

const styles = StyleSheet.create({
  card: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 14,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  info: { flex: 1, gap: 6 },
  head: { flexDirection: "row", alignItems: "center", gap: 8 },
  tight: { letterSpacing: -0.2 },
  tags: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  side: { alignItems: "flex-end", gap: 5, flexShrink: 0 },
  dbPill: { paddingVertical: 5, paddingHorizontal: 11, alignSelf: "flex-end" },
  change: {
    borderWidth: 1.5,
    borderColor: C.blueLine,
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 12,
  },
  chevron: { flexShrink: 0 },
});
